'use client';

import { format, isToday, parseISO } from 'date-fns';
import { useState } from 'react';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import { CALENDAR_TASK_STATUS } from '@/app/(main)/calendar/consts/calendar-task-status';
import { NEXT_UP_VISIBLE_BIRTHDAYS_COUNT } from '@/app/(main)/calendar/consts/next-up-birthdays';
import { useStopClickPropagation } from '@/app/(main)/calendar/hooks/useStopClickPropagation';
import { formatCalendarEventDisplayTitle } from '@/app/(main)/calendar/utils/formatCalendarEventDisplayTitle';
import type { CalendarSummaryGroups } from '@/app/(main)/calendar/utils/groupSummaryForDate';
import { getOutlookEventSummary } from '@/app/(main)/calendar/utils/parseOutlookEventDescription';
import Checkbox from '@/app/components/Checkbox';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { CALENDAR_PATH } from '@/consts/paths';
import type { CalendarEvent, CalendarViewMode, CalendarVisibilityState } from '@/types/calendarTypes';
import { CalendarNextUpContentSkeleton } from '../CalendarNextUpPanelSkeleton';
import { NextUpNavigation } from '../NextUpNavigation';
import BirthdayIcon from '/public/icons/birthday.svg';
import CalendarIcon from '/public/icons/calendar.svg';

type CalendarNextUpPanelProps = {
  onPrevious: () => void;
  onNext: () => void;
  view: CalendarViewMode;
  titleAnchor: Date;
  weekStart: Date;
  isLoading: boolean;
  weekEnd: Date;
  selectedDate: Date;
  summary: CalendarSummaryGroups;
  visibility: CalendarVisibilityState;
  onVisibilityChange: (next: CalendarVisibilityState) => void;
  onToggleTaskComplete?: (taskId: string, completed: boolean) => void;
  onTaskClick?: (taskId: string) => void;
  showFooterCta: boolean;
};

function EmptyBlock() {
  return (
    <p className="text-body-small text-grey-500 py-6 text-center border border-dashed border-grey-300 rounded-lg">
      Nothing scheduled for this date.
    </p>
  );
}

function formatEventTime(event: CalendarEvent): string {
  const start = parseISO(event.start);
  if (Number.isNaN(start.getTime())) return '';
  return format(start, 'h:mm a');
}

function sortEventsByStart(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    const aStart = parseISO(a.start).getTime();
    const bStart = parseISO(b.start).getTime();
    if (Number.isNaN(aStart) || Number.isNaN(bStart)) return 0;
    return aStart - bStart;
  });
}

type OutlookEventItemProps = {
  event: CalendarEvent;
  showTime?: boolean;
};

function OutlookEventItem({ event, showTime = true }: OutlookEventItemProps) {
  const summary = getOutlookEventSummary(event.description, event.address);

  return (
    <li className="pb-3">
      {showTime ? <div className="text-button-regular text-blue-900">{formatEventTime(event)}</div> : null}
      <div className={`flex items-start gap-1.5 ${showTime ? 'mt-1.5' : ''}`}>
        <span
          className={`h-2 w-2 shrink-0 rounded-full mt-1.25 ${
            event.type === CALENDAR_EVENT_TYPES.OUTLOOK_VIRTUAL ? 'bg-blue-200' : 'bg-blue-300'
          }`}
          aria-hidden
        />
        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="text-body-small medium text-black">{event.title}</div>
          {summary ? (
            <div className="mt-0.5 text-body-small text-grey-700 [overflow-wrap:anywhere]">{summary}</div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

type DeadlineTaskItemProps = {
  task: CalendarEvent;
  isCompleted: boolean;
  isClosed: boolean;
  onTaskClick?: (taskId: string) => void;
  onToggleTaskComplete?: (taskId: string, completed: boolean) => void;
};

function DeadlineTaskItem({ task, isCompleted, isClosed, onTaskClick, onToggleTaskComplete }: DeadlineTaskItemProps) {
  const checkboxWrapperRef = useStopClickPropagation<HTMLSpanElement>(() => {
    if (!task.taskId || !onToggleTaskComplete) return;
    onToggleTaskComplete(task.taskId, !isCompleted);
  });

  return (
    <li
      className={`sf-calendar-nextup-task flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-body-small transition-colors ${
        isClosed
          ? 'border-grey-200 bg-grey-100 text-grey-500 opacity-60'
          : 'border-grey-200 bg-white text-grey-800 hover:border-blue-200'
      }`}
      onClick={() => {
        if (!task.taskId || !onTaskClick) return;
        onTaskClick(task.taskId);
      }}
    >
      {onToggleTaskComplete && !isClosed ? (
        <span ref={checkboxWrapperRef} className="shrink-0">
          <Checkbox checked={isCompleted} size="small" readOnly tabIndex={-1} />
        </span>
      ) : (
        <CalendarIcon className="h-4 w-4 shrink-0 text-blue-300" aria-hidden />
      )}
      <span
        className={`min-w-0 truncate ${isClosed ? 'text-grey-500' : isCompleted ? 'line-through text-grey-500' : ''}`}
      >
        {formatCalendarEventDisplayTitle(task)}
      </span>
    </li>
  );
}

type BirthdaysListProps = {
  birthdays: CalendarEvent[];
};

function BirthdaysList({ birthdays }: BirthdaysListProps) {
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = birthdays.length > NEXT_UP_VISIBLE_BIRTHDAYS_COUNT;
  const hiddenBirthdaysCount = birthdays.length - NEXT_UP_VISIBLE_BIRTHDAYS_COUNT;
  const visibleBirthdays =
    shouldCollapse && !expanded ? birthdays.slice(0, NEXT_UP_VISIBLE_BIRTHDAYS_COUNT) : birthdays;

  return (
    <ul className="flex flex-wrap items-center gap-1.5">
      {visibleBirthdays.map((birthday) => (
        <li
          key={birthday.id}
          className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-[100px] bg-green-100 px-1.5 py-0.5 text-body-small text-status-success"
        >
          <BirthdayIcon className="h-4 w-4 shrink-0 text-status-success" aria-hidden />
          <span className="truncate">{formatCalendarEventDisplayTitle(birthday)}</span>
        </li>
      ))}
      {shouldCollapse ? (
        <li>
          <button
            type="button"
            className="px-1 py-0.5 text-body-small text-grey-700 hover:text-blue-900"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Show less' : `+${hiddenBirthdaysCount} more`}
          </button>
        </li>
      ) : null}
    </ul>
  );
}

export function CalendarNextUpPanel({
  view,
  titleAnchor,
  weekStart,
  weekEnd,
  isLoading,
  selectedDate,
  summary,
  visibility,
  onPrevious,
  onNext,
  onVisibilityChange,
  onToggleTaskComplete,
  onTaskClick,
  showFooterCta,
}: CalendarNextUpPanelProps) {
  const hasAny = summary.events.length > 0 || summary.birthdays.length > 0 || summary.tasks.length > 0;
  const reminderTasks = summary.tasks.filter((task) => task.type === CALENDAR_EVENT_TYPES.TASK_REMINDER);
  const deadlineTasks = summary.tasks.filter((task) => task.type === CALENDAR_EVENT_TYPES.TASK_DEADLINE);
  const timedEvents = sortEventsByStart(summary.events.filter((event) => !event.allDay));
  const allDayEvents = sortEventsByStart(summary.events.filter((event) => event.allDay));
  const hasEvents = timedEvents.length > 0 || allDayEvents.length > 0;
  const hasTasks = reminderTasks.length > 0 || deadlineTasks.length > 0;

  const toggle = (key: keyof CalendarVisibilityState) => {
    onVisibilityChange({ ...visibility, [key]: !visibility[key] });
  };

  return (
    <aside className="flex h-auto w-full shrink-0 flex-col self-stretch border border-grey-300 rounded-[14px] bg-white shadow-card md:h-full md:min-h-0">
      <NextUpNavigation
        onPrevious={onPrevious}
        onNext={onNext}
        view={view}
        titleAnchor={titleAnchor}
        weekStart={weekStart}
        weekEnd={weekEnd}
        isWidget={showFooterCta}
      />
      <div className="flex shrink-0 flex-col gap-1.5 md:px-5 px-4 md:pt-5 pt-1.5 pb-4">
        <div className="flex flex-col gap-0.5">
          <h3 className="md:block hidden text-button-semibold  text-black">Next Up</h3>
          <p className="text-body-small text-grey-700">Schedule for this day</p>
        </div>
        <div className="flex md:flex-col justify-between gap-2">
          <label className="flex items-center gap-2 cursor-pointer text-body-regular text-grey-800">
            <Checkbox size="small" checked={visibility.events} onChange={() => toggle('events')} />
            Events
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-body-regular text-grey-800">
            <Checkbox size="small" checked={visibility.tasks} onChange={() => toggle('tasks')} />
            Tasks
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-body-regular text-grey-800">
            <Checkbox size="small" checked={visibility.birthdays} onChange={() => toggle('birthdays')} />
            Birthdays
          </label>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-1 pb-5 md:min-h-0 md:flex-1">
        <div className="flex shrink-0 items-center w-full px-4">
          <span className="h-px bg-grey-300 min-w-1/4 flex-1" />
          <p className="whitespace-nowrap text-button-regular text-grey-700 w-fit px-3">
            {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d, yyyy')}
          </p>
          <span className="h-px bg-grey-300 min-w-1/4 flex-1" />
        </div>

        {isLoading ? (
          <div className="px-4">
            <CalendarNextUpContentSkeleton />
          </div>
        ) : !hasAny ? (
          <div className="px-4 md:min-h-0 md:flex-1">
            <EmptyBlock />
          </div>
        ) : (
          <div className="flex flex-col gap-2 px-4 md:min-h-0 md:flex-1 md:overflow-y-auto">
            {hasEvents ? (
              <div className="flex flex-col gap-2">
                {timedEvents.length > 0 ? (
                  <ul className="flex flex-col">
                    {timedEvents.map((event) => (
                      <OutlookEventItem key={event.id} event={event} />
                    ))}
                  </ul>
                ) : null}

                {allDayEvents.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-body-small text-blue-900">All-day</p>
                    <ul className="flex flex-col">
                      {allDayEvents.map((event) => (
                        <OutlookEventItem key={event.id} event={event} showTime={false} />
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}

            {hasTasks ? (
              <div className={`flex flex-col gap-2 ${hasEvents ? 'border-t border-grey-300 pt-2' : ''}`}>
                {reminderTasks.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {reminderTasks.map((task) => {
                      const isCompleted = task.status === CALENDAR_TASK_STATUS.COMPLETED;
                      const isClosed = task.status === CALENDAR_TASK_STATUS.CLOSED;

                      return (
                        <li
                          key={task.id}
                          className={`inline-flex w-fit max-w-full items-center gap-2 rounded-[4px] px-1.5 py-0.5 text-body-small ${
                            onTaskClick && task.taskId ? 'cursor-pointer' : ''
                          } ${
                            isClosed
                              ? 'bg-grey-100 text-grey-500 opacity-60'
                              : isCompleted
                                ? 'bg-red-300 text-grey-500 line-through'
                                : 'bg-red-300 text-blue-900'
                          }`}
                          onClick={() => {
                            if (!task.taskId || !onTaskClick) return;
                            onTaskClick(task.taskId);
                          }}
                        >
                          <CalendarIcon className="h-4 w-4 shrink-0 text-blue-300" aria-hidden />
                          <span className="truncate">{formatCalendarEventDisplayTitle(task)}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}

                {deadlineTasks.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-body-small text-blue-900">All-day tasks</p>
                    <ul className="flex flex-col gap-2">
                      {deadlineTasks.map((task) => {
                        const isCompleted = task.status === CALENDAR_TASK_STATUS.COMPLETED;
                        const isClosed = task.status === CALENDAR_TASK_STATUS.CLOSED;

                        return (
                          <DeadlineTaskItem
                            key={task.id}
                            task={task}
                            isCompleted={isCompleted}
                            isClosed={isClosed}
                            onTaskClick={onTaskClick}
                            onToggleTaskComplete={onToggleTaskComplete}
                          />
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}

            {summary.birthdays.length > 0 ? (
              <div className={hasEvents || hasTasks ? 'border-t border-grey-300 pt-2' : ''}>
                <BirthdaysList key={selectedDate.toISOString()} birthdays={summary.birthdays} />
              </div>
            ) : null}
          </div>
        )}

        {showFooterCta ? (
          <div className="shrink-0 px-4 pt-2">
            <BaseButton intent="secondary" className="w-full" href={CALENDAR_PATH}>
              View Full Calendar
            </BaseButton>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
